import * as React from "react"
import {useCallback} from "react"
import {useSignal} from "@preact/signals-react";
import {Box, Grid} from "@mui/material";
import {AntSwitchSeeker, ButtonLoadingSeeker, CheckboxSeeker, IconSeeker, CardSeeker} from "@ui-seeker/core";

import {FormControl, InputLabel, MenuItem, Select, Typography} from "@mui/material";
import {USER_ROLE} from "@constants";
import {useUserApp} from "@hooks";
import {MatchersFinishPage, MatchersNumItems} from "../@components";
import {useReverseMatcherTarget} from "./@hooks";
import {isLoadMoreItems, reverseMatcherItems, reverseMatcherGroupBy, reverseMatcherBatchGroupBy} from "./@signals";
import ReverseMatcherOptions from "./Options";
import ReverseMatcherTarget from "./Target";
import ReverseMatcherTableView from "./TableView";


export default function ReverseMatcherContent({ hasIdMapping = false }) {
	const { userRole } = useUserApp()
	const isAdmin = userRole === USER_ROLE.ADMIN
	
	const hideIACandidates = useSignal(false)
	const hideMappedProducts = useSignal(true)
	const isTableView = useSignal(false)
	const editingItem = useSignal(null)
	const editingFromTable = useSignal(false)
	const tableRemoveItemFn = useSignal(null)

	const {
		targetIndex, targetData, selectedSKU, setSelectedSKU, setTargetIndex, finishItems, saveMapping, needMorItems, handleMoreProducts,
		isLoadingMapping
	} = useReverseMatcherTarget()

	// Transformar item de tabla a formato de modo uno-a-uno
	const getTransformedEditingItem = () => {
		if (!editingItem.value) return null
		
		const item = editingItem.value
		
		// Si ya tiene AIOptions, retornar directamente
		if (item.AIOptions) return item
		
		// Si viene desde la tabla, buscar el item completo en reverseMatcherItems
		// que tiene todos los candidatos IA
		const fullItem = reverseMatcherItems.value.find(i => i.mappingID === item.mappingID)
		
		if (fullItem && fullItem.AIOptions) return fullItem
		
		// Fallback: Transformar aiCandidate (singular) a AIOptions (array)
		const aiOptions = item.aiCandidate ? [item.aiCandidate] : []
		
		return {
			...item,
			AIOptions: aiOptions
		}
	}

	// Wrapper para saveMapping que vuelve al modo tabla si estamos editando desde tabla
	const handleSaveMapping = useCallback((notFound = false) => {
		const currentEditingItem = editingItem.value
		saveMapping(notFound)
		
		if (editingFromTable.value && currentEditingItem) {
			// Eliminar el item de la tabla sin recargar
			if (tableRemoveItemFn.value) {
				tableRemoveItemFn.value(currentEditingItem.mappingID)
			}
			
			// Pequeño delay para que se complete la operación
			setTimeout(() => {
				editingItem.value = null
				editingFromTable.value = false
				isTableView.value = true
			}, 300)
		}
	}, [saveMapping])

	if (finishItems)
		return <MatchersFinishPage onClick={handleMoreProducts} />

    // If opened by id_mapping and no items were returned, show a helpful message
    if (hasIdMapping && Array.isArray(reverseMatcherItems.value) && reverseMatcherItems.value.length === 0) {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <CardSeeker>
                        <Box p={3} display="flex" flexDirection="column" alignItems="center" gap={1}>
                            <IconSeeker icon="circle-xmark" sx={{ fontSize: "2rem", color: 'text.disabled' }} />
                            <Box textAlign="center">
                                No item found for the provided id_mapping.
                            </Box>
                        </Box>
                    </CardSeeker>
                </Grid>
            </Grid>
        )
    }

	return <Grid container spacing={2}>
		<Grid item xs={12} display="flex" justifyContent="space-between">
			<Box display="flex">
				<MatchersNumItems
					version="reverse"
					isLoadedMoreItems={isLoadMoreItems.value}
				/>
			</Box>

			<Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
				{editingItem.value && !isTableView.value && (
					<ButtonLoadingSeeker
						color="secondary"
						size="small"
						startIcon={<IconSeeker icon="arrow-left" />}
						onClick={() => {
							editingItem.value = null
							editingFromTable.value = false
							isTableView.value = true
						}}
					>
						Back to Table
					</ButtonLoadingSeeker>
				)}

				{!hasIdMapping && (
                    <AntSwitchSeeker
                        trueText="Table View"
                        falseText="One by One"
                        checked={isTableView.value}
                        onChange={e => {
                            isTableView.value = e.target.checked
                            if (e.target.checked) {
                                editingItem.value = null
                            }
                        }}
                    />
                )}

				{!isTableView.value && <FormControl size="small" sx={{ minWidth: 160 }}>
					<InputLabel>Group By</InputLabel>
					<Select
						label="Group By"
						value={reverseMatcherGroupBy.value || ''}
						onChange={e => reverseMatcherGroupBy.value = e.target.value || null}
						disabled={!!reverseMatcherBatchGroupBy.value}
					>
						<MenuItem value="">No grouping</MenuItem>
						<MenuItem value="reference">Reference</MenuItem>
						<MenuItem value="productURL">Product URL</MenuItem>
					</Select>
					{reverseMatcherBatchGroupBy.value && (
						<Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
							(from batch)
						</Typography>
					)}
				</FormControl>}

				{/* <CheckboxSeeker
					label="Hide IA Candidates"
					checked={hideIACandidates.value}
					onChange={e => hideIACandidates.value = e.target.checked}
				/> */}

				{!isTableView.value && isAdmin && <CheckboxSeeker
					label="Hide Already Mapped Products"
					checked={hideMappedProducts.value}
					onChange={e => hideMappedProducts.value = e.target.checked}
				/>}

				{!isTableView.value && <ButtonLoadingSeeker
					color="error"
					loading={isLoadingMapping}
					disabled={needMorItems}
					startIcon={<IconSeeker icon="person-circle-question" />}
					onClick={() => handleSaveMapping(true)}
				>
					Not Found
				</ButtonLoadingSeeker>}


				{!isTableView.value && <ButtonLoadingSeeker
					color="success"
					loading={isLoadingMapping}
					startIcon={<IconSeeker icon="badge-check" />}
					disabled={selectedSKU === null || needMorItems}
					onClick={() => handleSaveMapping()}
				>
					Validate
				</ButtonLoadingSeeker>}
			</Box>
		</Grid>

		{/* Table View - Siempre montado pero oculto cuando no está activo */}
		<Grid item xs={12} sx={{ display: isTableView.value ? 'block' : 'none' }}>
			<ReverseMatcherTableView 
				onEditItem={(item) => {
					editingItem.value = item
					editingFromTable.value = true
					isTableView.value = false
				}}
				exposeRemoveItem={(removeItemFn) => {
					tableRemoveItemFn.value = removeItemFn
				}}
			/>
		</Grid>

		{/* One by One View - Oculto cuando está en modo tabla */}
		{!isTableView.value && (() => {
			const currentData = getTransformedEditingItem() || targetData
			return <>
				<Grid item xs={7} xl={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', maxHeight: "75vh" }}>
					<ReverseMatcherTarget
						targetIndex={targetIndex}
						data={currentData}
						handleChangeIndex={setTargetIndex}
						isLoadingMapping={isLoadingMapping}
					/>
				</Grid>

				<Grid item xs={5} xl={6}>
					<ReverseMatcherOptions
						showOptions={true}
						showMappedProducts={isAdmin && !hideMappedProducts.value}
						targetIndex={targetIndex}
						skuID={currentData.skuID}
						skuName={currentData.skuName}
						brandID={currentData.brandID}
						siteID={currentData.siteID}
						retailer={currentData.retailer}
						country={currentData.country}
						options={currentData.AIOptions || []}
						selected={selectedSKU}
						setSelectedSKU={setSelectedSKU}
						saveMapping={handleSaveMapping}
						groupBy={reverseMatcherGroupBy.value}
					/>
				</Grid>
			</>
		})()}
	</Grid>
}